import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import {
  Avatar, Alert, Box, Button, CircularProgress, Divider, Grid, IconButton,
  InputAdornment, Stack, TextField, Typography, FormControlLabel,
  MenuItem,
} from "@mui/material";
import ThemeSwitch from "components/form/ThemeSwitch";
import useFormValidation from "hooks/useFormValidation";
import { validators } from "utils/formValidation";
import {
  ArrowLeftOutlined, CloudUploadOutlined,
  PictureOutlined, UserOutlined,
} from "@ant-design/icons";

export default function DriverForm() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const [form, setForm] = useState({
    name:         "",
    username:     "",
    phone:        "",
    display_name: "",
    password:     "",
    latitude:     "",
    longitude:    "",
    status:       true,
    store_id:     "",
  });

  const [storesLoading, setStoresLoading] = useState(false);
  const [stores,          setStores]          = useState([]);
  const [profileFile,     setProfileFile]     = useState(null);
  const [documentFile,    setDocumentFile]    = useState(null);
  const [profilePreview,  setProfilePreview]  = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [submitting,      setSubmitting]      = useState(false);
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps } = useFormValidation();

  useEffect(() => {
    setStoresLoading(true);
    api.get("/store/list")
      .then((res) => setStores(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setStores([]))
      .finally(() => setStoresLoading(false));
  }, []);

  useEffect(() => {
    if (isEdit) fetchDriver();
  }, [id]);

  const fetchDriver = async () => {
    try {
      const res  = await api.get(`/driver/${id}`);
      const data = res.data.data;
      setForm({
        name:         data.user?.name      || "",
        username:     data.user?.username  || "",
        phone:        data.user?.phone     || "",
        display_name: data.display_name    || "",
        password:     "",
        latitude:     data.latitude        || "",
        longitude:    data.longitude       || "",
        status:       data.status          ?? true,
        store_id:     data.store_id        || "",
      });
      if (data.profileImage?.imageUrl) setProfilePreview(data.profileImage.imageUrl);
      if (data.document?.imageUrl)     setDocumentPreview(data.document.imageUrl);
    } catch (err) {
      console.error("[DriverForm] fetchDriver error:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name) clearFieldError(name);
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleProfileFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileFile(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const handleDocumentFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocumentFile(file);
    setDocumentPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const schema = {
      name: [validators.required("Name")],
      username: [validators.required("Username")],
      display_name: [validators.required("Display name")]
    };
    if (!isEdit) {
      schema.password = [
        validators.required("Password"),
        validators.minLength(6, "Password")
      ];
    } else if (form.password) {
      schema.password = [validators.minLength(6, "Password")];
    }

    if (!validate(schema, form)) return;

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name",         form.name.trim());
      fd.append("username",     form.username.trim());
      fd.append("display_name", form.display_name.trim());
      if (form.phone)     fd.append("phone",    form.phone.trim());
      if (form.latitude)  fd.append("latitude", form.latitude);
      if (form.longitude) fd.append("longitude",form.longitude);
      fd.append("store_id", form.store_id ?? "");

      if (!isEdit) {
        fd.append("password", form.password);
      } else {
        if (form.password) fd.append("password", form.password);
        fd.append("status", form.status);
      }

      if (profileFile)  fd.append("profile_image",   profileFile);
      if (documentFile) fd.append("driver_document", documentFile);

      if (isEdit) {
        await api.put(`/driver/edit/${id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/driver/register", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      navigate("/driver/list");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save driver";
      if (msg.toLowerCase().includes("password")) {
        setServerError(msg, "password");
      } else {
        setServerError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>

      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back"
        title={isEdit ? "Edit Driver" : "Register Driver"}
        subtitle={isEdit ? "Update driver profile and documents." : "Register a new delivery driver."}
      />

      {formError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      ) : null}

      <Grid container spacing={3}>

        {/* ── Profile Photo ── */}
        <Grid item xs={12} sm={6} md={3}>
          <MainCard title="Profile Photo" sx={{ height: "100%" }}>
            <Stack alignItems="center" spacing={2}>
              <Avatar
                src={profilePreview}
                sx={{ width: 110, height: 110, bgcolor: "primary.lighter" }}
              >
                {!profilePreview && <UserOutlined style={{ fontSize: 44 }} />}
              </Avatar>
              <Button variant="outlined" fullWidth component="label"
                startIcon={<CloudUploadOutlined />}>
                {profileFile ? profileFile.name : "Upload Photo"}
                <input type="file" hidden accept="image/*"
                  onChange={handleProfileFile} />
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                JPG, PNG, WEBP — max 5 MB
              </Typography>
            </Stack>
          </MainCard>
        </Grid>

        {/* ── Driver Document ── */}
        <Grid item xs={12} sm={6} md={3}>
          <MainCard title="Driver Document" sx={{ height: "100%" }}>
            <Stack alignItems="center" spacing={2}>
              <Box sx={{
                width: "100%", height: 110,
                border: "2px dashed", borderColor: "divider",
                borderRadius: 2, overflow: "hidden",
                display: "flex", alignItems: "center",
                justifyContent: "center", bgcolor: "grey.50",
              }}>
                {documentPreview ? (
                  <img src={documentPreview} alt="Document"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Stack alignItems="center" spacing={1} sx={{ color: "text.disabled" }}>
                    <PictureOutlined style={{ fontSize: 40 }} />
                    <Typography variant="caption">No document uploaded</Typography>
                  </Stack>
                )}
              </Box>
              <Button variant="outlined" fullWidth component="label"
                startIcon={<CloudUploadOutlined />}>
                {documentFile ? documentFile.name : "Upload Document"}
                <input type="file" hidden accept="image/*,application/pdf"
                  onChange={handleDocumentFile} />
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                License, ID card — JPG, PNG, PDF — max 10 MB
              </Typography>
            </Stack>
          </MainCard>
        </Grid>

        {/* ── RIGHT column: fields ── */}
        <Grid item xs={12} md={6}>
          <MainCard title="Driver Details">
            <Grid container spacing={2.5}>

              {/* Full Name */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required
                  label="Full Name" name="name"
                  value={form.name} onChange={handleChange}
                  placeholder="e.g. John Doe"
                  {...fieldErrorProps("name")} />
              </Grid>

              {/* Display Name */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required
                  label="Display Name" name="display_name"
                  value={form.display_name} onChange={handleChange}
                  placeholder="Shown to customers"
                  {...fieldErrorProps("display_name")}
                  helperText={fieldErrorProps("display_name").helperText || "Must be unique"} />
              </Grid>

              {/* Username */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required
                  label="Username" name="username"
                  value={form.username} onChange={handleChange}
                  placeholder="Used for login"
                  disabled={isEdit}
                  {...fieldErrorProps("username")}
                  helperText={fieldErrorProps("username").helperText || "Must be unique"} />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth
                  label="Phone" name="phone"
                  value={form.phone} onChange={handleChange}
                  placeholder="+91 9876543210" />
              </Grid>

              {/* Password */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth
                  required={!isEdit}
                  type="password"
                  label={isEdit ? "New Password (leave blank to keep)" : "Password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  {...fieldErrorProps("password")}
                  helperText={fieldErrorProps("password").helperText || (isEdit ? "Only fill if changing password" : "Min 6 characters")}
                />
              </Grid>

              {/* Store */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth
                  label="Assign Store (optional)"
                  name="store_id"
                  value={form.store_id}
                  onChange={handleChange}
                  helperText="Leave blank if not assigned to a store"
                  disabled={storesLoading}
                  InputProps={storesLoading ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  } : undefined}
                >
                  <MenuItem value=""><em>— No Store —</em></MenuItem>
                  {stores.map((s) => (
                    <MenuItem key={s.store_id} value={s.store_id}>
                      {s.title_en}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Status */}
              {isEdit && (
                <Grid item xs={12} sm={6}>
                  <Stack sx={{ pt: 1 }}>
                    <FormControlLabel
                      control={
                        <ThemeSwitch
                          checked={form.status}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, status: e.target.checked }))
                          }
                        />
                      }
                      label="Account Active"
                    />
                  </Stack>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider>
                  <Typography variant="caption" color="text.secondary">
                    Location (optional)
                  </Typography>
                </Divider>
              </Grid>

              {/* Latitude */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth
                  label="Latitude" name="latitude"
                  value={form.latitude} onChange={handleChange}
                  placeholder="e.g. 12.9716"
                  type="number" inputProps={{ step: "any" }} />
              </Grid>

              {/* Longitude */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth
                  label="Longitude" name="longitude"
                  value={form.longitude} onChange={handleChange}
                  placeholder="e.g. 77.5946"
                  type="number" inputProps={{ step: "any" }} />
              </Grid>

            </Grid>
          </MainCard>
        </Grid>

      </Grid>

      {/* ── Bottom action buttons ── */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        gap={1.5}
        sx={{ mt: 3, pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          disabled={submitting}
          sx={{ minWidth: 110 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={submitting}
          sx={{ minWidth: 160 }}
        >
          {submitting ? "Saving…" : isEdit ? "Update Driver" : "Register Driver"}
        </Button>
      </Stack>

    </Box>
  );
}